import { History, MessageSquarePlus, Send, Square, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { AdminAction, AdminAgentSession } from '../../../../services/adminAgent.service';
import { useAdminActions } from '../hooks/useAdminActions';
import { useAdminAgentChat } from '../hooks/useAdminAgentChat';
import { useAutoResizeTextarea } from '../hooks/useAutoResizeTextarea';
import { useConversationTitle } from '../hooks/useConversationTitle';
import { ActionApprovalCard } from './ActionApprovalCard';
import { AdminAgentMessage } from './AdminAgentMessage';
import { ConversationList } from './ConversationList';
import { TypingIndicator } from './TypingIndicator';

const MAX_RECENT_DECIDED = 3;
/** The composer grows with its content up to this height, then scrolls. */
const COMPOSER_MAX_HEIGHT_PX = 160;

interface AdminAgentPanelProps {
  session: AdminAgentSession;
  onNewConversation: () => void;
  onOpenConversation: (conversationPublicId: string) => void;
  onClose: () => void;
}

/** Tells the agent how the admin decided, so it can confirm the outcome with admin_action_status. */
function decisionMessage(decision: 'approved' | 'rejected', publicId: string): string {
  return `I ${decision} request ${publicId}. Check its status and tell me the result.`;
}

export const AdminAgentPanel: React.FC<AdminAgentPanelProps> = ({
  session,
  onNewConversation,
  onOpenConversation,
  onClose,
}) => {
  const { messages, sendMessage, stop, isStreaming, error } = useAdminAgentChat(session);
  const { actions, approve, reject } = useAdminActions({
    open: true,
    isStreaming,
    conversationPublicId: session.conversationPublicId,
  });
  const conversationTitle = useConversationTitle(session.conversationPublicId, {
    hasMessages: messages.length > 0,
    isStreaming,
  });
  const [draft, setDraft] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  useAutoResizeTextarea(composerRef, draft, COMPOSER_MAX_HEIGHT_PX);

  // Waiting for the first token: the last visible message is still the admin's own.
  const showTyping = isStreaming && messages[messages.length - 1]?.role !== 'assistant';

  const pending = actions.filter((action) => action.status === 'pending');
  const decided = actions
    .filter((action: AdminAction) => action.status !== 'pending')
    .slice(0, MAX_RECENT_DECIDED);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, pending.length, showTyping]);

  const submit = () => {
    if (!draft.trim() || isStreaming) return;
    void sendMessage(draft);
    setDraft('');
  };

  const handleApprove = (publicId: string, confirmed: boolean) =>
    approve.mutate(
      { publicId, confirmed },
      { onSuccess: () => void sendMessage(decisionMessage('approved', publicId)) }
    );

  const handleReject = (publicId: string) =>
    reject.mutate(publicId, {
      onSuccess: () => void sendMessage(decisionMessage('rejected', publicId)),
    });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 dark:text-slate-100">Admin Assistant</div>
          {conversationTitle && (
            <div
              className="truncate text-xs text-slate-500 dark:text-slate-400"
              title={conversationTitle}
            >
              {conversationTitle}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowHistory((current) => !current)}
            title="Conversations"
            aria-label="Conversations"
            aria-pressed={showHistory}
            className={`rounded-md p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 ${
              showHistory
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-slate-500 dark:text-slate-400'
            }`}
          >
            <History size={16} />
          </button>
          <button
            type="button"
            onClick={() => {
              setShowHistory(false);
              onNewConversation();
            }}
            title="New conversation"
            aria-label="New conversation"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
          >
            <MessageSquarePlus size={16} />
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close admin assistant"
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {showHistory ? (
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <ConversationList
            currentConversationId={session.conversationPublicId}
            onSelect={(id) => {
              setShowHistory(false);
              if (id !== session.conversationPublicId) onOpenConversation(id);
            }}
          />
        </div>
      ) : (
        <div className="min-h-0 min-w-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden px-4 py-3">
          {messages.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Ask me to look up or change data. Every change waits for your approval.
            </p>
          )}
          {messages.map((message, index) => (
            <AdminAgentMessage
              key={message.id}
              message={message}
              streaming={
                isStreaming && index === messages.length - 1 && message.role === 'assistant'
              }
            />
          ))}
          {showTyping && <TypingIndicator />}
          {[...pending, ...decided].map((action) => (
            <ActionApprovalCard
              key={action.publicId}
              action={action}
              busy={approve.isPending || reject.isPending}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
          {(approve.error || reject.error || error) && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {(approve.error || reject.error || error)?.message}
            </p>
          )}
          <div ref={endRef} />
        </div>
      )}

      <div className="flex items-end gap-2 border-t border-slate-200 p-3 dark:border-slate-700">
        <textarea
          ref={composerRef}
          value={draft}
          rows={1}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder="Message the assistant…"
          className="min-h-[38px] flex-1 resize-none rounded-lg border border-slate-300 bg-transparent px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-500 dark:border-slate-600 dark:text-slate-100"
        />
        <button
          type="button"
          onClick={isStreaming ? stop : submit}
          disabled={!isStreaming && !draft.trim()}
          className="rounded-lg bg-blue-500 p-2.5 text-white hover:bg-blue-600 disabled:opacity-50"
          aria-label={isStreaming ? 'Stop' : 'Send'}
        >
          {isStreaming ? <Square size={16} /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
};
